import { Track } from "livekit-client"

// Laisse l'OS rendre la caméra avant de la juger : au retour de l'arrière-plan,
// la capture met un instant à repartir.
const SETTLE_MS = 2000
const SAMPLE_GAP_MS = 2500

const defaultWait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * La piste caméra locale publiée, si elle existe et n'est pas coupée.
 * Une caméra que le tuteur a éteinte lui-même n'a rien à envoyer.
 */
export function publishedCameraTrack(room) {
    const publication =
        room?.localParticipant?.getTrackPublication?.(Track.Source.Camera)
    if (!publication || publication.isMuted) return null

    const track = publication.videoTrack ?? publication.track

    // Reconnue à ce qu'elle sait faire : le web et le natif n'exposent pas la
    // même classe, et une piste distante n'a ni stats d'envoi ni relance.
    return typeof track?.getSenderStats === "function" &&
        typeof track?.restartTrack === "function"
        ? track
        : null
}

/**
 * Ce que la caméra envoyait, agrégé sur les couches simulcast. `framesSent` est
 * cumulatif : c'est sa PROGRESSION entre deux lectures qui dit si des images
 * partent, jamais sa valeur seule.
 */
export async function readCameraSendStats(track) {
    try {
        const layers = (await track?.getSenderStats?.()) ?? []
        if (layers.length === 0) return null

        // getSenderStats trie la couche la plus large en tête.
        const [top] = layers

        return {
            framesSent: layers.reduce((sum, l) => sum + (l.framesSent ?? 0), 0),
            fps: top.framesPerSecond ?? 0,
            width: top.frameWidth ?? 0,
            height: top.frameHeight ?? 0,
            layers: layers.filter((l) => (l.framesPerSecond ?? 0) > 0).length,
            limitation: top.qualityLimitationReason ?? null,
        }
    } catch (e) {
        return null
    }
}

/** Les stats d'envoi telles qu'elles voyagent avec un `quality_degraded`. */
export function videoStatsPayload(before, after) {
    if (!after) return null

    return {
        frames_sent_delta:
            before && after.framesSent >= before.framesSent
                ? after.framesSent - before.framesSent
                : null,
        fps: after.fps,
        width: after.width,
        height: after.height,
        layers: after.layers,
        limitation: after.limitation,
    }
}

/**
 * Avec `dynacast`, LiveKit coupe lui-même l'envoi des couches que personne ne
 * regarde (`encoding.active = false`) : tuteur seul dans la salle, vignette du
 * tuteur masquée chez l'élève. Plus aucune image ne part alors que la caméra
 * marche : ce silence-là ne dit rien de la caméra.
 */
function sendingPausedByLiveKit(track) {
    try {
        const encodings = track?.sender?.getParameters?.()?.encodings
        return (
            Array.isArray(encodings) &&
            encodings.length > 0 &&
            encodings.every((encoding) => encoding.active === false)
        )
    } catch (e) {
        return false
    }
}

/**
 * Un seul appel à la fois : un appel qui arrive pendant qu'un autre tourne
 * reçoit la même promesse au lieu d'en lancer un second.
 */
export function singleFlight(fn) {
    let inFlight = null

    return (...args) => {
        if (!inFlight) {
            inFlight = Promise.resolve()
                .then(() => fn(...args))
                .finally(() => {
                    inFlight = null
                })
        }
        return inFlight
    }
}

/**
 * Au retour dans l'app, la caméra locale envoie-t-elle encore des images ?
 *
 * Sur Android, une caméra reprise de l'arrière-plan peut rester publiée sans
 * plus rien émettre : les élèves voient une image figée, LiveKit juge la
 * connexion « poor », et l'état ne se débloque qu'à la recréation de la piste.
 * On ne relance donc QUE dans ce cas — des images qui partent, même mal, ne
 * justifient pas de couper la caméra du tuteur en pleine séance.
 *
 * @returns {Promise<boolean>} vrai si la caméra a été relancée
 */
export async function restartCameraIfItStoppedSending(room, { wait = defaultWait } = {}) {
    const track = publishedCameraTrack(room)
    if (!track) return false

    await wait(SETTLE_MS)
    const before = await readCameraSendStats(track)
    if (!before) return false

    await wait(SAMPLE_GAP_MS)
    const after = await readCameraSendStats(track)
    if (!after || after.framesSent > before.framesSent) return false
    if (sendingPausedByLiveKit(track)) return false

    try {
        await track.restartTrack()
    } catch (e) {
        console.log("Camera restart failed", e)
        return false
    }

    return true
}
